import React, { useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  Button,
  Avatar,
  Divider,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import SaveIcon from "@mui/icons-material/Save";
import { useAuth } from "../../context/AuthContext";
import { useColorMode } from "../../context/ThemeContext";
import { api } from "../../utils/api";

export default function Settings() {
  const { user } = useAuth();
  const { mode, setMode } = useColorMode();

  const [name, setName] = useState(user?.name || "");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const saveProfile = async () => {
    setSaving(true);
    setMsg(null);
    try {
      await api.post("/users/update_profile.php", { name, password });
      setMsg({ type: "success", text: "Profile updated successfully" });
      setPassword("");
    } catch (err) {
      setMsg({ type: "error", text: err.message || "Update failed" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Avatar sx={{ bgcolor: "primary.main", width: 48, height: 48 }}>
          <SettingsIcon />
        </Avatar>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your profile and application preferences
          </Typography>
        </Box>
      </Stack>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
          Profile
        </Typography>

        {msg && (
          <Alert severity={msg.type} sx={{ mb: 2 }}>
            {msg.text}
          </Alert>
        )}

        <Stack spacing={2}>
          <TextField
            label="Full name"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            label="Change password"
            type="password"
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Leave blank if not changing"
          />
          <Box>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={saveProfile}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save Changes"}
            </Button>
          </Box>
        </Stack>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
          Appearance
        </Typography>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Theme mode
          </Typography>
          <ToggleButtonGroup
            value={mode}
            exclusive
            size="small"
            onChange={(_e, val) => val && setMode(val)}
          >
            <ToggleButton value="light">
              <LightModeIcon fontSize="small" sx={{ mr: 0.5 }} /> Light
            </ToggleButton>
            <ToggleButton value="dark">
              <DarkModeIcon fontSize="small" sx={{ mr: 0.5 }} /> Dark
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        <Divider sx={{ my: 3 }} />
        <Typography variant="caption" color="text.disabled">
          * More preferences coming soon.
        </Typography>
      </Paper>
    </Box>
  );
}
